// Phone number validation and formatting utilities

export interface PhoneValidationResult {
  isValid: boolean;
  formatted?: string;
  error?: string;
}

/**
 * Validates a phone number and formats it as (XXX)XXX-XXXX.
 */
export function validatePhoneNumber(phone: string): PhoneValidationResult {
  if (!phone || phone.trim() === '') {
    return {
      isValid: false,
      error: 'Phone number is required'
    };
  }

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Check if it's exactly 10 digits
  if (digits.length !== 10) {
    return {
      isValid: false,
      error: 'Phone number must be exactly 10 digits'
    };
  }
  
  return {
    isValid: true,
    formatted: formatPhoneNumber(phone)
  };
}

/**
 * Formats a phone number as (XXX)XXX-XXXX.
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return '';

  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) {
    return phone;
  }

  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Normalizes a phone number (validates and formats)
 */
export function normalizePhoneNumber(phone: string): PhoneValidationResult {
  const validation = validatePhoneNumber(phone);
  
  if (!validation.isValid) {
    return validation;
  }
  
  return {
    isValid: true,
    formatted: validation.formatted
  };
}

/**
 * Formats phone number as user types (for input field)
 */
export function formatPhoneNumberInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length === 0) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Masks a first name for security display (e.g., "Bob" -> "XXX")
 */
export function maskFirstName(firstName: string): string {
  if (!firstName || firstName.trim() === '') {
    return 'XXX';
  }
  
  // Replace all characters with X's, keeping the same length
  return 'X'.repeat(firstName.length);
}

/**
 * Masks a phone number for security display (shows only last 4 digits)
 * e.g., "(123)456-7890" -> "(XXX)XXX-7890"
 */
export function maskPhoneNumber(phone: string): string {
  if (!phone || phone.trim() === '') {
    return '(XXX)XXX-XXXX';
  }

  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) {
    return '(XXX)XXX-XXXX';
  }

  return `(XXX)XXX-${digits.slice(-4)}`;
}
